/**
 * "Watch live" (ENG-2470): Support asked to "check an RTSP stream via VLC".
 * Next to "Test stream", this opens the camera's live video as our servers
 * receive it, in a dialog, with the Live View player from the Alerts page
 * (LiveStreamPlayer + useWhepLiveStream): the same stream-session mint and
 * renewals, CDN cookies, "Starting camera stream…" cold-start state, idle
 * "Still watching?" prompt, quota / overage messages and the "Use
 * production" notice on non-prod builds. Nothing streaming is reimplemented
 * here; the player only runs while the dialog is open.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { LiveStreamPlayer } from "../../Alerts/LiveStreamPlayer";
import {
  isSiteLiveStreamingOn,
  useCanUserWatchLive,
  useSiteLiveStreamingEnabled,
} from "./watchLiveAvailability";

export const WATCH_LIVE_LABEL = "Watch live";
export const WATCH_LIVE_SUBTITLE = "Live video as our servers receive it";
export const WATCH_LIVE_EXPLAINER =
  "This is the camera's live video exactly as our system receives it, so if it plays here, we are getting video from the camera. Starting a stream can take up to a minute. Watching counts toward your live streaming allowance.";

const ASPECT = 9 / 16;
const DEFAULT_WIDTH = 820;

/**
 * The player takes pixel sizes; fit it to the dialog's content width. A
 * callback ref, because the dialog mounts its content in a portal a render
 * after `open` flips.
 */
const usePlayerWidth = () => {
  const [node, setNode] = React.useState<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(DEFAULT_WIDTH);
  React.useEffect(() => {
    if (!node) return;
    const measure = () => {
      const w = node.clientWidth;
      if (w > 0) setWidth(Math.floor(w));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [node]);
  return { ref: setNode, width };
};

export const WatchLiveDialog = ({
  open,
  onClose,
  cameraId,
  cameraName,
}: {
  open: boolean;
  onClose: () => void;
  cameraId: number;
  cameraName?: string | null;
}) => {
  const { ref, width } = usePlayerWidth();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943", pr: 6 }}
      >
        {WATCH_LIVE_LABEL}
        {cameraName ? `: ${cameraName}` : ""}
        <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
          {WATCH_LIVE_SUBTITLE}
        </span>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <p
          className="m-0 mb-3 font-mulish text-[13px] text-[#4c5257]"
          data-testid="watch-live-explainer"
        >
          {WATCH_LIVE_EXPLAINER}
        </p>
        <div ref={ref} className="w-full" data-testid="watch-live-player">
          {open && (
            <LiveStreamPlayer
              cameraId={String(cameraId)}
              enabled={open}
              width={width}
              height={Math.round(width * ASPECT)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const WatchLiveButton = ({
  cameraId,
  cameraName,
  siteId,
  siteLiveStreamingEnabled,
  size = "sm",
  variant = "outline",
}: {
  cameraId: number;
  cameraName?: string | null;
  /** Used to read the site's live-streaming switch when not passed in. */
  siteId?: number | null;
  /** The site About's `live_streaming_enabled`, when the caller has it. */
  siteLiveStreamingEnabled?: boolean | null;
  size?: "sm" | "md";
  variant?: "outline" | "quiet";
}) => {
  const [open, setOpen] = React.useState(false);
  const userCan = useCanUserWatchLive();
  const siteEnabled = useSiteLiveStreamingEnabled(
    siteId,
    siteLiveStreamingEnabled,
    !userCan
  );
  if (!userCan || !Number.isFinite(cameraId)) return null;
  // Still reading the site: hide rather than flash a button that may go.
  if (siteEnabled === undefined || !isSiteLiveStreamingOn(siteEnabled)) {
    return null;
  }
  return (
    <>
      <TsButton
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        data-testid={`watch-live-button-${cameraId}`}
      >
        {WATCH_LIVE_LABEL}
      </TsButton>
      <WatchLiveDialog
        open={open}
        onClose={() => setOpen(false)}
        cameraId={cameraId}
        cameraName={cameraName}
      />
    </>
  );
};
